import styled from '@emotion/styled';
import { useRef } from 'react';
import { useButton, useOverlayTrigger } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';

import { mergeRefs } from '../../lib/mergeRefs';

import { HomeyUtils } from '../../store/HomeyUtils';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useApi } from '../../store/SelectedHomeyStore.hooks';

import { su, suPx } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { motion } from '../../theme/motion.generated';
import { theme } from '../../theme/theme';

import { AnimationRemain } from '../../components/animation/AnimationRemain';
import { Glyph } from '../../components/common/Glyph';
import { Menu, MenuItem } from '../../components/common/Menu';
import { Overlay } from '../../components/overlay/Overlay';

export function DevicesMoreMenu({ hasDevices, onHiddenDevicesPress }) {
  const { i18n } = useI18n();
  const { api } = useApi();

  const supportsHiddenDevices = HomeyUtils.getHasHiddenDevices(api?.homey);

  const triggerRef = useRef();
  const triggerState = useOverlayTriggerState({ defaultOpen: false });
  const overlayTrigger = useOverlayTrigger({ type: 'menu' }, triggerState, triggerRef);

  return (
    <>
      <MoreButton {...overlayTrigger.triggerProps} ref={triggerRef} onPress={triggerState.toggle} />

      <AnimationRemain condition={triggerState.isOpen} delay={200}>
        {(animationRemainProps) => {
          return (
            <Overlay
              targetRef={triggerRef}
              overlayProps={overlayTrigger.overlayProps}
              overlayTriggerState={triggerState}
              offset={suPx(1)}
              animationRemainProps={animationRemainProps}
              autoFocus={true}
              restoreFocus={true}
            >
              <S.MenuRoot>
                <Menu
                  onAction={(key) => {
                    triggerState.close();

                    switch (key) {
                      case KEYS.HIDDEN_DEVICES:
                        onHiddenDevicesPress();
                        break;
                      default:
                        break;
                    }
                  }}
                  aria-label="Devices Menu"
                >
                  {supportsHiddenDevices && (
                    <MenuItem
                      key={KEYS.HIDDEN_DEVICES}
                      isDisabled={!hasDevices}
                      textValue={i18n.messageFormatter('devices.hiddenDevices')}
                    >
                      <S.MenuItemContent>
                        <Glyph
                          source={glyph.core.squaresOutline}
                          size={theme.icon.size_18}
                          color={theme.color.icon_dark}
                        />
                        <S.MenuItemLabel>
                          {i18n.messageFormatter('devices.hiddenDevices')}
                        </S.MenuItemLabel>
                      </S.MenuItemContent>
                    </MenuItem>
                  )}
                </Menu>
              </S.MenuRoot>
            </Overlay>
          );
        }}
      </AnimationRemain>
    </>
  );
}

const KEYS = {
  HIDDEN_DEVICES: 'HIDDEN_DEVICES',
};

function MoreButton(props) {
  const internalRef = useRef();
  const button = useButton(props, internalRef);

  return (
    <S.Button {...button.buttonProps} ref={mergeRefs([internalRef, props.ref])}>
      <Glyph
        source={glyph.core.tripleDot}
        size={theme.icon.size_default}
        color="var(--module-button-glyph-color)"
      />
    </S.Button>
  );
}

const S = (DevicesMoreMenu.S = class S {
  static Button = styled.button`
    --module-button-glyph-color: ${theme.color.icon_light};

    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: ${theme.color.component};
    box-shadow: ${theme.boxShadow.default};
    border-radius: 50%;
    cursor: pointer;
    transition: all ${motion.duration.fast} ${motion.curve.fast_in};

    &:hover {
      box-shadow: ${theme.boxShadow.hover};
      --module-button-glyph-color: ${theme.color.icon_light_hover};
    }
  `;

  static MenuRoot = styled.div`
    padding: ${su(1)} ${su(1)};
  `;

  static MenuItemContent = styled.div`
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: ${su(1)} ${su(1)};
    color: ${theme.color.text};
  `;

  static MenuItemLabel = styled.span`
    display: inline-block;
    margin-inline-start: 10px;
  `;
});
